<template>
  <div
    class="side-nav"
    :class="[sidenavCollapsed && 'collapsed']"
  >
    <div class="side-nav-fixed">
      <div
        class="toggle"
        :title="sidenavCollapsed ? 'Expand' : 'Collapse'"
        @click="toggleSidenav()"
      >
        <span v-if="sidenavCollapsed">&gt;</span>
        <span v-if="!sidenavCollapsed">&lt;</span>
      </div>

      <div class="collapser">
        <div class="content">
          <div
            class="icon"
            @click="$router.push({ path: '/Dashboard' })"
          >
            <img :src="webeoLogo">
          </div>

          <ul>
            <template v-for="item in items">
              <li
                v-if="!item.hide"
                :key="item.name"
                :title="item.name"
              >
                <router-link
                  :to="item.path"
                  @click.native="handleNavClick(item)"
                >
                  <div class="item">
                    <div><img :src="item.icon"></div>
                    <span>{{ item.name }}</span>
                  </div>
                </router-link>
              </li>
            </template>
          </ul>

          <div class="buttons">
            <div>
              <div v-if="!isCreatingCampaign">
                <button
                  title="Create a campaign"
                  class="create-campaign-circle"
                  @click="$router.push('/Campaigns/Create')"
                >
                  +
                </button>

                <button
                  class="create-campaign"
                  @click="$router.push('/Campaigns/Create')"
                >
                  Create A Campaign
                </button>
              </div>
            </div>

            <div
              class="support-group"
              :class="{ 'has-impersonation': isImpersonator }"
            >
              <div>
                <button
                  class="ask-question"
                  @click="showZendeskWidget"
                >
                  {{ $t('sideNav.supportRequest') }}
                </button>

                <button
                  v-if="hasClientFeature('Feedback')"
                  class="howToGuides"
                  @click="openHowToGuides"
                >
                  {{ $t('sideNav.howToGuides') }}
                </button>

                <div
                  v-if="isImpersonator"
                  class="impersonation"
                >
                  <div class="impersonation-icon">
                    <div class="icon-circle">
                      <font-awesome-icon icon="user-secret" />
                    </div>
                  </div>
                  <span
                    class="name"
                  >
                    {{ $t('sideNav.impersonation.impersonating') }}
                  </span>
                  <button
                    class="change-client-button"
                    @click="$router.push('/SelectClient')"
                  >
                    {{ $t('sideNav.impersonation.switch') }}
                  </button>
                </div>
              </div>

              <div :class="{'no-pointer-events': !sidenavCollapsed}">
                <button
                  :title="$t('sideNav.askAQuestion')"
                  class="ask-question-circle"
                  @click="showZendeskWidget"
                >
                  ?
                </button>

                <button
                  v-if="hasClientFeature('Feedback')"
                  :title="$t('sideNav.howToGuides')"
                  class="howToGuides-circle"
                  @click="openHowToGuides"
                >
                  <img src="../assets/feedback-icon.svg">
                </button>

                <button
                  v-if="isImpersonator"
                  :title="$t('sideNav.impersonation.switch')"
                  class="impersonation-circle"
                  @click="$router.push('/SelectClient')"
                >
                  <font-awesome-icon icon="user-secret" />
                  <font-awesome-icon icon="random" />
                </button>
              </div>
            </div>
          </div>

          <div
            id="__profile"
            class="profile"
          >
            <div
              class="container"
              :class="{ active: profileMenuActive }"
              data-test="profile_container_button"
              @click="profileMenuActive = !profileMenuActive"
            >
              <div class="picture">
                <img src="../assets/profile-icon.svg">
              </div>

              <div class="name-area">
                <span class="name">
                  {{ clientFirstName }}
                </span>

                <span class="client-name">
                  {{ clientName }}
                </span>
              </div>
            </div>
            <div
              class="menu-content"
              :class="{ active: profileMenuActive }"
            >
              <div
                v-if="hasClientFeature('ProfileUI')"
                data-test="profile_menu_profile"
                @click="$router.push('/Profile'); profileMenuActive = false;"
              >
                <font-awesome-icon
                  icon="user-cog"
                  class="profile-menu-icon"
                />
                {{ $t('sideNav.profile') }}
              </div>
              <div
                v-if="hasClientFeature('IntegrationsUI')"
                data-test="profile_menu_connected_apps"
                @click="$router.push('/Integrations'); profileMenuActive = false;"
              >
                <font-awesome-icon
                  icon="cogs"
                  class="profile-menu-icon"
                />
                Connected Apps
              </div>
              <div
                v-if="hasClientFeature('UserManagementUI') && hasPermission('user-management')"
                data-test="profile_menu_user_management"
                @click="$router.push('/Users'); profileMenuActive = false;"
              >
                <font-awesome-icon
                  icon="users"
                  class="profile-menu-icon"
                />
                {{ $t('sideNav.userManagement') }}
              </div>
              <div
                v-if="hasClientFeature('DarkTheme')"
                @click="toggleTheme()"
              >
                <font-awesome-icon
                  icon="moon"
                  class="profile-menu-icon"
                  :class="{ 'light-theme-icon': theme === 'light-theme'}"
                />
                Toggle Theme
              </div>
              <div
                class="menu-border-top"
                data-test="profile_menu_log_out"
                @click="logout"
              >
                <font-awesome-icon
                  icon="sign-out-alt"
                  class="profile-menu-icon"
                />
                {{ $t('sideNav.logout') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

const webeoLogo = require('../assets/webeo-logo.png');
const iconHome = require('../assets/home-icon.svg');
const iconCampaigns = require('../assets/campaigns-icon.svg');
const iconAudiences = require('../assets/audiences-icon.svg');
const iconExperiences = require('../assets/experiences-icon.svg');
const iconEventTracking = require('../assets/event-tracking-icon.svg');
const iconReporting = require('../assets/reporting-icon.svg');

const webeoLogoDark = require('../assets-dark/webeo-logo.png');
const iconHomeDark = require('../assets-dark/home-icon.svg');
const iconCampaignsDark = require('../assets-dark/campaigns-icon.svg');
const iconAudiencesDark = require('../assets-dark/audiences-icon.svg');
const iconExperiencesDark = require('../assets-dark/experiences-icon.svg');
const iconEventTrackingDark = require('../assets-dark/event-tracking-icon.svg');
const iconReportingDark = require('../assets-dark/reporting-icon.svg');

export default {
  data: () => ({
    collapsed: false,
    profileMenuActive: false,
  }),
  computed: {
    ...mapGetters([
      'clientFirstName',
      'clientName',
      'hasClientFeature',
      'sidenavCollapsed',
      'isImpersonator',
      'isLightTheme',
      'userPermissions',
    ]),
    items() {
      return [
        {
          name: 'Home',
          icon: this.isLightTheme ? iconHome : iconHomeDark,
          path: '/Dashboard',
          hide: !this.hasClientFeature('DashboardUI'),
        },
        {
          name: 'Home V2',
          icon: this.isLightTheme ? iconHome : iconHomeDark,
          path: '/ReportingV2/Home',
          hide: !(
            this.hasClientFeature('WebeoReportingV2')
            && this.hasClientFeature('WebeoReportingV2.3-Homepage')
          ),
        },
        {
          name: 'Campaigns',
          icon: this.isLightTheme ? iconCampaigns : iconCampaignsDark,
          path: '/Campaigns',
          hide: !this.hasClientFeature('CampaignsUI'),
        },
        {
          name: 'Audiences',
          icon: this.isLightTheme ? iconAudiences : iconAudiencesDark,
          path: '/Audiences',
          hide: !this.hasClientFeature('AudienceUI'),
        },
        {
          name: 'Experiences',
          icon: this.isLightTheme ? iconExperiences : iconExperiencesDark,
          path: '/Experiences',
          hide: this.hasClientFeature('CampaignFlow'),
        },
        {
          name: 'Tracking',
          icon: this.isLightTheme ? iconEventTracking : iconEventTrackingDark,
          path: '/Tracking',
          hide: !this.hasClientFeature('EventTrack'),
        },
        {
          name: 'Reporting',
          icon: this.isLightTheme ? iconReporting : iconReportingDark,
          path: '/Reporting',
          hide: !this.hasClientFeature('ReportDash') && !this.hasClientFeature('PIReports'),
        },
        {
          name: 'Reporting V2',
          icon: this.isLightTheme ? iconReporting : iconReportingDark,
          path: this.hasClientFeature('WebeoReportingV2.4-Performance')
            ? '/ReportingV2/Overall-Performance'
            : '/ReportingV2/Campaign',
          hide: !this.hasClientFeature('WebeoReportingV2'),
        },
      ];
    },
    webeoLogo() {
      return this.isLightTheme ? webeoLogo : webeoLogoDark;
    },
    isCreatingCampaign() {
      const campaignCreatePrefix = '/campaigns/create';
      return this.$route.path.toLowerCase().includes(campaignCreatePrefix);
    },
  },
  created() {
    document.addEventListener('click', this.profileMenuClickHandler);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.profileMenuClickHandler);
  },
  methods: {
    ...mapActions([
      'logout',
      'toggleSidenav',
      'toggleTheme',
    ]),
    handleNavClick(item) {
      if (item.name !== 'Reporting V2') return;
      if (window.reportingV2 && window.reportingV2.router) {
        window.reportingV2.router.push(item.path);
      }
    },
    showZendeskWidget() {
      window.zE.activate();
    },
    openHowToGuides() {
      window.open('https://www.webeo.com/howtoguides', '_blank');
    },
    profileMenuClickHandler(e) {
      this.profileMenuActive = document.getElementById('__profile').contains(e.target)
        ? this.profileMenuActive : false;
    },
    hasPermission(permission) {
      return this.userPermissions.includes(permission);
    },
  },
};
</script>

<style scoped>
@media only screen and (max-height: 825px) {
  .content .item > div {
    height: 35px;
  }
}
.side-nav, .side-nav * {
  transition: 0.2s ease-in-out;
}
.side-nav {
  position: relative;
  user-select: none;
  height: 100%;
  width: 300px;
  min-width: 300px;
  max-width: 300px;
  z-index: 1032;
}
.side-nav-fixed {
  position: fixed;
  background-color: var(--webeo-white);
  left: 0;
  top: 0;
  height: 100%;
  z-index: 500;
}
.dark-theme .side-nav-fixed {
  background-color: var(--webeo-background-2);
}
.collapser {
  width: 300px;
  max-width: 300px;
  height: 100%;
  border-right: 1px solid var(--webeo-grey100);
  overflow-x: hidden;
  overflow-y: auto;
}
.dark-theme .collapser {
  border-right: none;
}
.content {
  min-width: 300px;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.toggle {
  position: absolute;
  border: 2px solid var(--webeo-grey100);
  background-color: var(--webeo-white);
  height: 36px;
  width: 36px;
  border-radius: 100px;
  margin-right: 10px;
  top: 32px;
  left: 280px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 25px;
  cursor: pointer;
}
.dark-theme .toggle {
  border: 2px solid var(--webeo-magenta);
  background-color: var(--webeo-background-2);
}
.toggle:hover {
  background-color: var(--webeo-grey100);
}
.dark-theme .toggle:hover {
  background-color: var(--webeo-magenta);
}

.icon {
  padding: 20px 34px 25px 36px;
  height: 55px;
  cursor: pointer;
}
.icon > img {
  height: 100%;
}

ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
}
li {
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 0.3px;
  color: var(--webeo-purple);
  cursor: pointer;
}
.dark-theme li {
  color: var(--webeo-foreground);
}
li a {
  text-decoration: none;
}
li .router-link-active > .item {
  background-color: var(--webeo-cyan200);
}
.dark-theme li .router-link-active > .item {
  background-color: var(--webeo-background);
}
li .item:hover,
li .item:active {
  background-color: var(--webeo-grey100);
}
.dark-theme li .item:hover,
.dark-theme li .item:active {
  background-color: var(--webeo-background-3);
}
.item {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 2px 20px;
}
.item > div {
  display: flex;
  height: 50px;
  min-width: 80px;
  width: 80px;
  padding: 8px;
}
.item > div > img {
  width: 100%;
}
.dark-theme .item > span {
  color: var(--webeo-foreground);
}

.buttons {
  position: relative;
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
button {
  height: 40px;
  font-size: 14px;
  border: none;
  outline: none;
  border-radius: 5px;
  border: 1px solid var(--webeo-grey100);
  cursor: pointer;
  text-transform: uppercase;
}
button:hover {
  background-color: var(--webeo-grey150);
  border: 1px solid var(--webeo-grey170);
}
button.howToGuides {
  margin-top: 18px;
}
button.create-campaign {
  color: var(--webeo-white);
  background-color: var(--webeo-magenta);
  border: none;
  width: 100%;
}
button.create-campaign:hover {
  background-color: var(--webeo-magenta-hover);
}
button.ask-question-circle, button.howToGuides-circle, button.impersonation-circle {
  opacity: 0;
  margin: 0 8px;
  border-radius: 50%;
  width: 45px;
  height: 45px;
  font-size: 20px;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
}
button.create-campaign-circle {
  color: var(--webeo-white);
  background-color: var(--webeo-magenta);
  position: absolute;
  opacity: 0;
  border-radius: 50%;
  margin: 0px 8px;
  width: 45px;
  height: 45px;
  font-size: 20px;
  font-weight: bold;
  border: none;
}
button.create-campaign-circle:hover {
  background-color: var(--webeo-magenta);
  border: none;
}
button.howToGuides-circle, button.impersonation-circle {
  margin-top: 15px;
}
button.howToGuides-circle img {
  height: 25px;
  width: 25px;
}

/* Profile Section Styles */
.profile > .container {
  position: relative;
  max-height: 70px;
  border-top: 1px solid var(--webeo-grey100);
  background-color: var(--webeo-background-2);
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 15px;
  overflow: hidden;
  cursor: pointer;
}
.dark-theme .profile > .container {
  background-color: var(--webeo-background-3);
  color: var(--webeo-white);
}
.profile > .container:hover {
  background-color: var(--webeo-grey100);
}
.dark-theme .profile > .container:hover {
  background-color: var(--webeo-accent-background-2);
}
.profile > .container.active {
  background-color: var(--webeo-cyan200);
}
.dark-theme .profile > .container.active {
  background-color: var(--webeo-accent-background-2);
}
.profile > .container > .picture {
  min-height: 70px;
  max-height: 70px;
  min-width: 70px;
  border-radius: 100px;
  margin-right: 14px;
}
.profile > .container > .name-area > .name {
  display: block;
  font-size: 20px;
  font-weight: bold;
  color: var(--webeo-links);
}
.dark-theme .profile > .container > .name-area > .name {
  color: var(--webeo-white);
}
.profile > .menu-content {
  visibility: hidden;
  position: fixed;
  background-color: var(--webeo-white);
  box-shadow: 4px 8px 20px 0px rgba(0,0,0,0.1);
  border: 1px solid var(--webeo-grey170);
  border-radius: 5px;
  min-width: 280px;
  max-width: 280px;
  bottom: 110px;
  left: 9px;
  max-height: 0;
  overflow: hidden;
}
.dark-theme .menu-content {
  background-color: var(--webeo-background-3);
  border: 1px solid var(--webeo-white);
}
.profile .menu-content.active {
  max-height: 100%;
  visibility: visible;
}
.profile .profile-menu-icon {
  width: 20px;
  margin-right: 8px;
  color: var(--webeo-black);
}
.dark-theme .profile .profile-menu-icon {
  color: var(--webeo-white);
}
.profile > .menu-content > div {
  padding: 12px 22px 12px 16px;
  font-size: 16px;
  color: var(--webeo-black);
  text-decoration: none;
  display: block;
  cursor: pointer;
}
.dark-theme .profile > .menu-content > div {
  color: var(--webeo-white);
}
.profile > .menu-content > div:hover {
  background-color: var(--webeo-grey100);
}
.dark-theme .profile > .menu-content > div:hover {
  background-color: var(--webeo-accent-background-2);
}
.profile > .menu-content > div:active {
  background-color: var(--webeo-grey100);
}
.dark-theme .profile > .menu-content > div:active {
  background-color: var(--webeo-accent-background-2);
}
.profile > .menu-content > .menu-border-top {
  border-top: 3px solid var(--webeo-accent-background);
}
/* Support Buttons Styles */
.support-group {
  position: relative;
  min-height: 98px;
  margin-top: 30px;
}
.dark-theme .support-group button {
  background-color: var(--webeo-background-3);
  color: var(--webeo-white);
  border-color: var(--webeo-background-3);
}
.dark-theme .support-group button:hover {
  background-color: var(--webeo-accent-background-2);
}
.support-group.has-impersonation {
  min-height: 157px;
}
.support-group > div {
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 0;
  width: 100%;
}
.impersonation {
  display: flex;
  border-top: 1px solid;
  background-color: var(--webeo-grey500);
  margin: -20px;
  margin-top: 20px;
}
.impersonation > .impersonation-icon {
  padding: 15px 10px 15px 15px;
  text-align: center;
  color: var(--webeo-grey500);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
}
.impersonation > .impersonation-icon > .icon-circle {
  height: 30px;
  width: 30px;
  background-color: #bbb;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.impersonation > .name {
  padding: 10px 10px 10px 10px;
  color: var(--webeo-white);
  font-size: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.impersonation > .change-client-button {
  margin: 20px;
  font-size: 14px;
  height: 20px;
  width: 85px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.impersonation-circle {
  background-color: var(--webeo-grey500);
  color: var(--webeo-white);
}
.impersonation-circle:hover > :first-child {
  display: none;
}
.impersonation-circle:not(:hover) > :last-child {
  display: none;
}
.no-pointer-events {
  pointer-events:none
}

/* Collapsed Styles */
.collapsed.side-nav {
  min-width: 100px;
  max-width: 100px;
}
.collapsed .collapser {
  min-width: 100px;
  max-width: 100px;
}
.collapsed .toggle {
  left: 80px;
}
.collapsed .icon {
  padding-left: 18px;
}
.collapsed .item {
  padding: 2px 0px;
}
.collapsed .item > div {
  padding: 8px 10px;
}
.collapsed .create-campaign {
  opacity: 0;
  pointer-events: none;
}
.collapsed .create-campaign-circle,
.collapsed .ask-question-circle,
.collapsed .howToGuides-circle,
.collapsed .impersonation-circle {
  opacity: 1;
}
.collapsed .ask-question, .collapsed .howToGuides {
  opacity: 0;
  pointer-events: none;
}
.collapsed .profile .name-area {
  opacity: 0;
}
.collapsed .profile .menu-content {
  min-width: 0;
}
.collapsed .impersonation {
  opacity: 0;
}
</style>
